import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo, faTrash } from '@fortawesome/free-solid-svg-icons';
import { trackPromise } from 'react-promise-tracker';
import Swal from 'sweetalert2';
import moment from 'moment';
import caAPI from '../../../../services/caAPI';
import DommusToggle from '../../../../components/DommusToggle';
import DommusTabelaPaginadaControlada from '../../../../components/DommusTabelaPaginadaControlada';
import { successToast } from '../../../../components/DommusToast';
import { extrairDados, obterMensagemErro } from '../../GestaoAgentesIa/utils';

const ROTULOS_TIPO = {
  bom: <span className="badge badge-success">Bom</span>,
  ruim: <span className="badge badge-danger">Ruim</span>,
};

export default function ExemplosAprendizado({ idBaseConhecimento, aberto }) {
  const [listaExemplos, setListaExemplos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(15);
  const [totalExemplos, setTotalExemplos] = useState(0);
  const [erroExemplos, setErroExemplos] = useState('');

  const handleCarregarExemplos = useCallback((paginaSolicitada = pagina, tamanhoSolicitado = tamanhoPagina) => {
    return trackPromise(
      caAPI.baseConhecimento.listarExperiencias(idBaseConhecimento, {
        page: paginaSolicitada,
        per_page: tamanhoSolicitado,
      })
        .then(resposta => {
          setListaExemplos(extrairDados(resposta));
          setTotalExemplos(resposta?.data?.total || 0);
          setErroExemplos('');
        })
        .catch(erro => setErroExemplos(obterMensagemErro(erro, 'Erro ao carregar os exemplos de aprendizado.')))
    );
  }, [idBaseConhecimento, pagina, tamanhoPagina]);

  useEffect(() => {
    if (!aberto || !idBaseConhecimento) return;
    handleCarregarExemplos(pagina);
  }, [aberto, idBaseConhecimento, handleCarregarExemplos, pagina, tamanhoPagina]);

  function handleAlterarTamanhoPagina(novoTamanho) {
    setPagina(1);
    setTamanhoPagina(Number(novoTamanho) || 15);
  }

  function handleTentarNovamente(exemplo) {
    trackPromise(
      caAPI.experienciaAprendizado.tentarNovamente(exemplo.id_experiencia_aprendizado)
        .then(() => {
          successToast.fire({ text: 'Reprocessamento do exemplo solicitado.' });
          return handleCarregarExemplos(pagina);
        })
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao reprocessar o exemplo.'),
          icon: 'error',
        }))
    );
  }

  function handleExcluirExemplo(exemplo) {
    Swal.fire({
      titleText: 'Remover exemplo de aprendizado?',
      text: 'Deseja remover este exemplo? Ele deixará de influenciar o Agente.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
    }).then(resultado => {
      if (!resultado.isConfirmed) return;
      trackPromise(
        caAPI.experienciaAprendizado.destroy(exemplo.id_experiencia_aprendizado)
          .then(() => {
            successToast.fire({ text: 'Exemplo removido com sucesso.' });
            return handleCarregarExemplos(pagina);
          })
          .catch(erro => Swal.fire({
            titleText: 'Erro',
            text: obterMensagemErro(erro, 'Erro ao remover o exemplo.'),
            icon: 'error',
          }))
      );
    });
  }

  const cabecalhoExemplos = (
    <tr>
      <th>Tipo</th>
      <th>Autor</th>
      <th>Data</th>
      <th>Atendimento</th>
      <th>Justificativa</th>
      <th>Status</th>
      <th className="text-center">Ações</th>
    </tr>
  );

  const linhasExemplos = listaExemplos.map(exemplo => (
    <tr key={exemplo.id_experiencia_aprendizado}>
      <td>{ROTULOS_TIPO[exemplo.tipo] || exemplo.tipo}</td>
      <td>{exemplo.autor || '-'}</td>
      <td>{exemplo.criado_em ? moment(exemplo.criado_em).format('DD/MM/YYYY HH:mm') : '-'}</td>
      <td>{exemplo.id_atendimento}</td>
      <td>{exemplo.justificativa}</td>
      <td>{exemplo.status}</td>
      <td className="text-center">
        <ButtonGroup size="sm">
          {exemplo.status === 'erro' && (
            <Button type="button" variant="outline-primary" title="Tentar novamente" aria-label={`Tentar novamente exemplo ${exemplo.id_experiencia_aprendizado}`} onClick={() => handleTentarNovamente(exemplo)}>
              <FontAwesomeIcon icon={faRedo} />
            </Button>
          )}
          <Button type="button" variant="outline-danger" title="Remover exemplo" aria-label={`Remover exemplo ${exemplo.id_experiencia_aprendizado}`} onClick={() => handleExcluirExemplo(exemplo)}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </ButtonGroup>
      </td>
    </tr>
  ));

  return (
    <DommusToggle title="Exemplos de aprendizado" toggle>
      {erroExemplos ? (
        <Alert variant="danger">{erroExemplos}</Alert>
      ) : listaExemplos.length ? (
        <DommusTabelaPaginadaControlada
          linhasCabecalho={cabecalhoExemplos}
          linhasCorpo={linhasExemplos}
          optionsTabela={{}}
          pagina={pagina}
          setPagina={setPagina}
          tamanhoPagina={tamanhoPagina}
          setTamanhoPagina={handleAlterarTamanhoPagina}
          tamanhoTotal={totalExemplos}
        />
      ) : (
        <Alert variant="info">Nenhum exemplo de aprendizado registrado nesta Base.</Alert>
      )}
    </DommusToggle>
  );
}
